import {
  getPositionsValuesFormatted,
  getHoldingsValuesFormatted,
  getOptionsValueFormatted,
} from "./portfolioKeyValueFormatter";

/**
 * Calculate position metrics with proper PnL calculations
 * 
 * @param {Object} position - The position object with trade data
 * @param {number} ltp - Last traded price
 * @param {string} positionType - "historical" or "live"
 * @param {boolean} isBackOfficeEnabled - Whether back office is enabled
 * @param {string} oms - Order management system identifier
 * @returns {Object} Formatted position with PnL calculations
 */
export function getFormattedPosition(
  position,
  ltp,
  positionType = "historical",
  isBackOfficeEnabled = false,
  oms = "TRADELAB"
) {
  const formattedPosition = getPositionsValuesFormatted(position);
  
  // Ensure LTP has a valid value
  const validLTP = (ltp !== undefined && ltp !== null && ltp > 0) 
    ? ltp 
    : (formattedPosition.previous_close || 0);
  
  // Ensure previous_close is not zero
  if (formattedPosition.previous_close === undefined || 
      formattedPosition.previous_close === null || 
      formattedPosition.previous_close === 0) {
    formattedPosition.previous_close = validLTP;
  }
  
  const multiplier = formattedPosition.multiplier || 1;
  let netBuyQty = formattedPosition.buy_quantity;
  let netSellQty = formattedPosition.sell_quantity;
  let netBuyAmount = formattedPosition.buy_amount;
  let netSellAmount = formattedPosition.sell_amount;
  let validity = "DAY";
  
  // Initialize PnL variables
  let totalPnL = 0;
  let realizedPnL = 0

  let unrealizedPnL = 0;
  let investedValue = 0;
  
  let buyVal, sellVal, actualBuyVal, actualSellVal;
  let actualNetBuyAmount, actualNetSellAmount;
  let actualOverallBuyAveragePrice, actualOverallSellAveragePrice;
  let avgPriceHistorical, avgPriceDaywise;
  
  // Historical/CF calculations
  if (positionType === "historical") {
    validity = "CF";
    netBuyQty += formattedPosition.cf_buy_quantity;
    netSellQty += formattedPosition.cf_sell_quantity;
    netBuyAmount += formattedPosition.cf_buy_amount;
    netSellAmount += formattedPosition.cf_sell_amount;

    // Calculate average price for historical positions
    const netAmountHistorical = (Math.abs(netBuyAmount) - Math.abs(netSellAmount));
    
    if (formattedPosition.net_quantity !== 0) {
      avgPriceHistorical = netAmountHistorical / (formattedPosition.net_quantity * multiplier);
    } else {
      avgPriceHistorical = 0;
    }

    // Calculate buy/sell values
    actualNetBuyAmount = formattedPosition.buy_amount + formattedPosition.cf_buy_amount;
    actualNetSellAmount = formattedPosition.sell_amount + formattedPosition.cf_sell_amount;
    
    // Calculate average prices
    actualOverallBuyAveragePrice = netBuyQty !== 0 ? 
      (actualNetBuyAmount / (netBuyQty * multiplier)) : 0;
    
    actualOverallSellAveragePrice = netSellQty !== 0 ? 
      (actualNetSellAmount / (netSellQty * multiplier)) : 0;
    
    // Calculate buy/sell values with proper multiplier
    actualBuyVal = actualOverallBuyAveragePrice * netBuyQty * multiplier;
    actualSellVal = actualOverallSellAveragePrice * netSellQty * multiplier;
    
    // Calculate realized PnL (squared-off quantity)
    const squaredOffQty = Math.min(netBuyQty, netSellQty);
    
    // If backend provides realized P&L, use it directly
    if (formattedPosition.realized_mtm !== undefined && formattedPosition.realized_mtm !== null) {
      realizedPnL = Number(formattedPosition.realized_mtm);
    } 
    // Otherwise calculate realized P&L
    else {
      // Check for RMTM prices first (more accurate for realized PnL)
      if (formattedPosition.rmtm_sell_average_price && formattedPosition.rmtm_buy_average_price) {
        realizedPnL = (formattedPosition.rmtm_sell_average_price - formattedPosition.rmtm_buy_average_price) * 
          squaredOffQty * multiplier;
          console.log("realizedPnL",realizedPnL);
      } else {
        realizedPnL = (actualOverallSellAveragePrice - actualOverallBuyAveragePrice) * 
          squaredOffQty * multiplier;
      }
      realizedPnL = (actualOverallSellAveragePrice - actualOverallBuyAveragePrice) * 
          squaredOffQty * multiplier;
    }
    
    // Special case: If net_quantity is 0 but we have buy/sell activity, this is a fully squared off position
    // In this case, realizedPnL should be the difference between sell and buy amounts
    if (formattedPosition.net_quantity === 0 && netBuyQty > 0 && netSellQty > 0) {
      // If realized_mtm not provided by backend, calculate it from the difference in amounts

        if (formattedPosition.net_amount !== undefined && formattedPosition.net_amount !== null) {
          // Use net_amount directly as realizedPnL if available
          realizedPnL = Number(formattedPosition.net_amount);
        } else {
          // Otherwise calculate from buy/sell amounts
          realizedPnL = actualNetSellAmount - actualNetBuyAmount;
        }
      
      
      // Set unrealizedPnL to 0 explicitly for squared off positions
      unrealizedPnL = 0;
    }
    // Otherwise calculate unrealized P&L for open positions
    else if (formattedPosition.net_quantity !== 0) {
      // If backend provides unrealized P&L, use it directly
      if (formattedPosition.unrealized_mtm !== undefined && formattedPosition.unrealized_mtm !== null) {
        unrealizedPnL = Number(formattedPosition.unrealized_mtm);
      } 
      // Otherwise calculate unrealized P&L
      else {
        // Calculate unrealized PnL based on current price
        const price = validLTP || formattedPosition.previous_close;
        
        if (formattedPosition.net_quantity > 0) {
          // Long position - current value minus cost basis
          unrealizedPnL = formattedPosition.net_quantity * (price - actualOverallBuyAveragePrice) * multiplier;
        } else if (formattedPosition.net_quantity < 0) {
          // Short position - proceeds from sale minus current value
          unrealizedPnL = Math.abs(formattedPosition.net_quantity) * (actualOverallSellAveragePrice - price) * multiplier;
        }
      }
    }
    
    // Calculate properly signed invested value
    if (formattedPosition.net_quantity !== 0) {
      const netQtyAbs = Math.abs(formattedPosition.net_quantity);
      investedValue = formattedPosition.net_quantity > 0 ? 
        (actualOverallBuyAveragePrice * netQtyAbs * multiplier) : 
        (actualOverallSellAveragePrice * netQtyAbs * multiplier);
    }
    
    // If backend provides total P&L, use it
    if (formattedPosition.pnl !== undefined && formattedPosition.pnl !== null) {
      totalPnL = Number(formattedPosition.pnl);
    } else {
      // Total PnL is realized + unrealized
      totalPnL = realizedPnL + unrealizedPnL;
    }
  }
  
  // Day-wise calculations
  if (formattedPosition.net_quantity !== 0) {
    avgPriceDaywise = formattedPosition.net_amount / (formattedPosition.net_quantity * multiplier);
  } else {
    avgPriceDaywise = 0;
  }
  
  // Calculate regular buy/sell averages
  const overallBuyAveragePrice = netBuyQty !== 0 ? 
    netBuyAmount / (netBuyQty * multiplier) : 0;
  
  const overallSellAveragePrice = netSellQty !== 0 ? 
    netSellAmount / (netSellQty * multiplier) : 0;
  
  // Calculate buy/sell values
  buyVal = overallBuyAveragePrice * netBuyQty * multiplier;
  sellVal = overallSellAveragePrice * netSellQty * multiplier;
  
  // Calculate MTM (Mark to Market) - This is the day's PnL
  const price = validLTP || formattedPosition.previous_close;
  let mtm = 0;
  
  // Special calculation for squared off positions (net_quantity = 0)
  if (formattedPosition.net_quantity === 0) {
    // If we have both buy and sell activity, this is a fully squared off position
    if ((netBuyQty > 0 && netSellQty > 0) || 
        (formattedPosition.buy_quantity > 0 && formattedPosition.sell_quantity > 0) ||
        (formattedPosition.cf_buy_quantity > 0 && formattedPosition.cf_sell_quantity > 0)) {
      
      // 1. If backend provides realized P&L, use it directly
      if (formattedPosition.realized_mtm !== undefined && formattedPosition.realized_mtm !== null) {
        realizedPnL = Number(formattedPosition.realized_mtm);
      }
      // 2. Otherwise, if net_amount is available, use it
      if (formattedPosition.net_amount !== undefined && formattedPosition.net_amount !== null) {
        realizedPnL = Number(formattedPosition.net_amount);
      }
      // 3. Otherwise calculate from buy/sell values
      // else {
        // Use RMTM values if available
        if (formattedPosition.rmtm_sell_average_price && formattedPosition.rmtm_buy_average_price) {
          const squaredOffQty = Math.min(netBuyQty, netSellQty);
          realizedPnL = (formattedPosition.rmtm_sell_average_price - formattedPosition.rmtm_buy_average_price) * 
            squaredOffQty * multiplier;
        } 
        // Otherwise use netSellValue - netBuyValue
        else {
          realizedPnL = actualNetSellAmount - actualNetBuyAmount;
        }
      // }
      
      // For closed positions, unrealized P&L is always 0
      unrealizedPnL = 0;
      
      // Total P&L is just the realized P&L for closed positions
      realizedPnL = 0;
      totalPnL = realizedPnL;
      
      // MTM is also equal to realized P&L for closed positions
      mtm = realizedPnL;
    }
  }
  
  // For day positions, use RMTM values if available for accurate calculation
  if (positionType !== "historical") {
    const daySquaredOffQty = Math.min(formattedPosition.buy_quantity, formattedPosition.sell_quantity);
    
    if (formattedPosition.net_quantity === 0 && 
        formattedPosition.rmtm_sell_average_price && 
        formattedPosition.rmtm_buy_average_price) {
      // For squared off positions
      mtm = (formattedPosition.sell_quantity * formattedPosition.rmtm_sell_average_price * multiplier -
             formattedPosition.buy_quantity * formattedPosition.rmtm_buy_average_price * multiplier);
      
      // realizedPnL = mtm;
      unrealizedPnL = 0;
    } else {
      // For open positions
      mtm = sellVal - buyVal + formattedPosition.net_quantity * price * multiplier;
      
      // Calculate day realized PnL
      if (formattedPosition.realized_mtm !== undefined && formattedPosition.realized_mtm !== null) {
        // realizedPnL = Number(formattedPosition.realized_mtm);
      } else if (formattedPosition.rmtm_sell_average_price && formattedPosition.rmtm_buy_average_price) {
        // realizedPnL = (formattedPosition.rmtm_sell_average_price - formattedPosition.rmtm_buy_average_price) * 
        //   daySquaredOffQty * multiplier;
      } else {
        // realizedPnL = (overallSellAveragePrice - overallBuyAveragePrice) * daySquaredOffQty * multiplier;
      }
      
      // Calculate day unrealized PnL
      if (formattedPosition.unrealized_mtm !== undefined && formattedPosition.unrealized_mtm !== null) {
        unrealizedPnL = Number(formattedPosition.unrealized_mtm);
      } else {
        unrealizedPnL = mtm - realizedPnL;
      }
    }
    
    // Day positions' total PnL 
    if (formattedPosition.pnl !== undefined && formattedPosition.pnl !== null) {
      totalPnL = Number(formattedPosition.pnl);
    } else {
      totalPnL = mtm;
    }
  }
  
  // Final object with all calculations
  return {
    ...formattedPosition,
    ltp: validLTP,
    mtm: realizedPnL || 0,
    realizedPnL:0,
    unrealizedPnL: unrealizedPnL || 0,
    pnl: totalPnL || 0,
    validity,
    blocked_margin: position?.blocked_margin,
    netBuyQty,
    netSellQty,
    investedValue,
    avgPrice: (positionType === "historical") ? Math.abs(avgPriceHistorical) : Math.abs(avgPriceDaywise),
    mtm_percent_change: investedValue !== 0 ? (unrealizedPnL / Math.abs(investedValue)) * 100 : 0,
    pnl_percent_change: investedValue !== 0 ? (totalPnL / Math.abs(investedValue)) * 100 : 0,
    buyAverage: overallBuyAveragePrice,
    actualBuyAverage: (positionType === "historical" ? actualOverallBuyAveragePrice : overallBuyAveragePrice) || 0,
    sellAverage: overallSellAveragePrice,
    actualSellAverage: (positionType === "historical" ? actualOverallSellAveragePrice : overallSellAveragePrice) || 0,
    netBuyValue: isBackOfficeEnabled && positionType === "historical" ? actualNetBuyAmount : netBuyAmount,
    netSellValue: isBackOfficeEnabled && positionType === "historical" ? actualNetSellAmount : netSellAmount,
    realised_mtm: realizedPnL || 0,  // For backward compatibility
    unrealised_mtm: unrealizedPnL || 0,  // For backward compatibility
  };
}

export function getFormattedOptions(options) {
  const formattedOptions = getOptionsValueFormatted(options);
  return {
    ...formattedOptions,
  };
}

export function getFormattedHolding(holding, oms, brokerName, quantityConfig) {
  const formattedHolding = getHoldingsValuesFormatted(holding);
  let available_quantity;
  let max_pledge_qty = 0;
  let max_authorize_qty = 0;
  if (oms === 'TRADELAB') {
    // console.log(oms);
    available_quantity =
      formattedHolding.free_quantity +
      formattedHolding.pending_quantity;
    max_authorize_qty =
      formattedHolding.free_quantity +
      formattedHolding.pending_quantity -
      formattedHolding.used_quantity;

    if (quantityConfig && quantityConfig.shouldIncludePledgeQuantity) {
      available_quantity += formattedHolding.pledge_quantity
    }

    if (quantityConfig && quantityConfig.shouldIncludeUsedQuantity) {
      available_quantity -= formattedHolding.used_quantity;
    }

    if (formattedHolding.used_quantity > formattedHolding.pending_quantity) {
      max_pledge_qty =
        formattedHolding.free_quantity +
        formattedHolding.pending_quantity -
        formattedHolding.used_quantity -
        formattedHolding.today_pledge_quantity;
    } else {
      max_pledge_qty = formattedHolding.free_quantity - formattedHolding.today_pledge_quantity;
    }

  } else if (brokerName === 'TRUSTLINE') {
    available_quantity =
      formattedHolding.quantity +
      formattedHolding.t1_quantity -
      formattedHolding.used_quantity;
    max_authorize_qty = available_quantity;
    // Math.abs(formattedHolding.withheld_qty);
  } else {
    // console.log(oms);
    available_quantity =
      formattedHolding.quantity +
      formattedHolding.t1_quantity -
      formattedHolding.used_quantity;
    max_authorize_qty = available_quantity;
  }

  const mtm =
    (formattedHolding.ltp - formattedHolding.previous_close) *
    available_quantity;
  const mtm_percent_change =
    (mtm / (available_quantity * formattedHolding.buy_avg)) * 100;
  const buy_price = formattedHolding.actual_buy_avg * available_quantity;
  const sell_price = formattedHolding.ltp * available_quantity;
  const pnl = sell_price - buy_price;
  const pnl_percent_change = (pnl / buy_price) * 100;
  return {
    ...formattedHolding,
    available_quantity,
    mtm,
    mtm_percent_change: mtm_percent_change || 0,
    pnl,
    pnl_percent_change: Number.isFinite(pnl_percent_change)
      ? pnl_percent_change
      : 0,
    max_pledge_qty,
    max_authorize_qty
  };
}
